// Is this tender still worth showing to someone looking for work?
//
// Two cases, and the second one is the reason this module exists.
//
// A published deadline settles it: past means closed. Roughly two thirds of
// what we hold is already in that state and correctly hidden.
//
// A MISSING deadline used to mean "show it forever", on the honest-looking
// reasoning that silence is not evidence of closure. The result was 426
// tenders pinned to the map regardless of age, 226 of them published more
// than three months ago: on a map of 823 pins, a quarter were near-certainly
// dead, and a bidder who clicks one and finds a procedure that closed in
// February stops trusting the other 597.
//
// So silence gets a horizon, and the horizon is measured rather than
// guessed. Across 3 348 stored tenders that carry BOTH dates, the gap from
// publication to deadline is a median of 26 days, 90 % close within 42, 95 %
// within 54 and 99 % within 78. Ninety days therefore sits beyond the 99th
// percentile: a notice with no published deadline that is older than that
// has, on this data, essentially no chance of still being open.
//
// Nothing is deleted. The rows stay and remain the basis of buyer history
// and of the price references, which is exactly what an archive is for.

export const STALE_WITHOUT_DEADLINE_DAYS = 90;

export interface BiddableFields {
  deadline?: string | null;
  publication_date?: string | null;
}

/** ISO timestamp before which an undated notice counts as closed. */
export function staleCutoffIso(now: number = Date.now()): string {
  return new Date(now - STALE_WITHOUT_DEADLINE_DAYS * 86_400_000).toISOString();
}

/**
 * Client-side mirror of the SQL predicate below. Kept next to it on purpose:
 * a list and a map that disagree about what is open is worse than either
 * rule on its own.
 */
export function isBiddable(t: BiddableFields, now: number = Date.now()): boolean {
  if (t.deadline) return new Date(t.deadline).getTime() >= now;
  // No deadline: fall back to age. No publication date either means we know
  // nothing at all about its age, and guessing "closed" would hide a notice
  // that may be days old.
  if (!t.publication_date) return true;
  return new Date(t.publication_date).getTime() >= now - STALE_WITHOUT_DEADLINE_DAYS * 86_400_000;
}

/**
 * The same rule as a PostgREST `or` expression, so the database does the
 * filtering for queries that should not pull the archive over the wire.
 */
export function biddableOrExpression(now: number = Date.now()): string {
  return [
    `deadline.gte.${new Date(now).toISOString()}`,
    `and(deadline.is.null,publication_date.gte.${staleCutoffIso(now)})`,
    `and(deadline.is.null,publication_date.is.null)`,
  ].join(",");
}
